import cx from 'clsx';
import * as React from 'react';

import { useObservedProperty } from '../../coreUtils/hooks';
import type { HotkeyString } from '../../coreUtils/hotkey';
import { useTranslation } from '../../coreUtils/i18n';
import { Debouncer } from '../../coreUtils/scheduler';

import { useCanvas } from '../../diagram/canvasApi';
import { useCanvasHotkey } from '../../diagram/canvasWidget';
import type { Rect, Size, Vector } from '../../diagram/geometry';

import { DraggableHandle } from '../utility/draggableHandle';
import { Dropdown } from '../utility/dropdown';
import { useSearchInputStore } from '../utility/searchInput';
import { getParentDockAlignment } from '../utility/viewportDock';

import { useWorkspace } from '../../workspace/workspaceContext';
import { UnifiedSearchTopic } from '../../workspace/commandBusTopic';

import { UnifiedSearchSectionContext } from './searchSection';

/**
 * Props for {@link UnifiedSearch} component.
 *
 * @see {@link UnifiedSearch}
 */
export interface UnifiedSearchProps {
    /**
     * Available sections (providers) to search with.
     */
    sections: ReadonlyArray<UnifiedSearchSection>;
    /**
     * Search dropdown expand direction.
     *
     * @default "down"
     */
    direction?: 'down' | 'up';
    /**
     * Default (initial) width for the component, including
     * the search input and the dropdown.
     *
     * @default 300
     */
    defaultWidth?: number;
    /**
     * Default (initial) height for the component
     * when the dropdown is expanded.
     *
     * @default 350
     */
    defaultHeight?: number;
    /**
     * Minimum width for the dropdown when resized.
     *
     * @default 200
     */
    minWidth?: number;
    /**
     * Minimum height for the dropdown when resized.
     *
     * @default 150
     */
    minHeight?: number;
    /**
     * Offset from right side of the canvas viewport to limit max width.
     *
     * @default 20
     */
    offsetWithMaxWidth?: number;
    /**
     * Offset from bottom side of the canvas viewport to limit max width.
     *
     * @default 20
     */
    offsetWithMaxHeight?: number;
    /**
     * Placeholder text for the search input.
     *
     * @default "Search for..."
     */
    placeholder?: string;
    /**
     * Keyboard hotkey to focus on the search input.
     *
     * Passing `null` disables the default hotkey.
     *
     * @default null
     */
    hotkeyFocus?: HotkeyString | null;
}

/**
 * Section (provider) to search with in {@link UnifiedSearch}.
 *
 * @see {@link UnifiedSearchProps}
 */
export interface UnifiedSearchSection {
    /**
     * Unique section key (arbitrary string).
     */
    readonly key: string;
    /**
     * Section label to display in the search.
     */
    readonly label: React.ReactNode;
    /**
     * Section title to display on hover.
     */
    readonly title?: string;
    /**
     * Component to provide and display search results.
     *
     * To access provided search context, use {@link useUnifiedSearchSection} hook.
     */
    readonly component: React.ReactNode;
}

/**
 * Events for {@link UnifiedSearch} event bus.
 *
 * @see {@link UnifiedSearch}
 */
export interface UnifiedSearchCommands {
    /**
     * Can be triggered to focus on the unified search input and switch to
     * the specified section if needed.
     */
    focus: {
        /**
         * Key of the section to activate.
         */
        readonly sectionKey?: string;
    };
}

interface SectionWithContext extends UnifiedSearchSection {
    readonly context: UnifiedSearchSectionContext;
}

const CLASS_NAME = 'reactodia-unified-search';

/**
 * Component to display a search input with a dropdown for results.
 *
 * One or many available search sections (providers) can be specified via
 * {@link UnifiedSearchProps.sections sections} prop.
 *
 * @category Components
 */
export function UnifiedSearch(props: UnifiedSearchProps) {
    const {
        sections,
        direction = 'down',
        defaultWidth = 300,
        defaultHeight = 350,
        minWidth = 200,
        minHeight = 150,
        offsetWithMaxWidth = 20,
        offsetWithMaxHeight = 20,
        hotkeyFocus,
    } = props;

    const [expanded, setExpanded] = React.useState(false);
    const [activeSection, setActiveSection] = React.useState(ActiveSection.empty);

    const findDefaultSectionKey = (): string | undefined => {
        if (sections.some(section => section.key === activeSection.previousActive)) {
            return activeSection.previousActive;
        } else if (sections.length > 0) {
            return sections[0].key;
        } else {
            return undefined;
        }
    };

    const searchStore = useSearchInputStore({initialValue: ''});
    const searchTerm = useObservedProperty(
        searchStore.events, 'changeValue', () => searchStore.value
    );

    React.useEffect(() => {
        const onClearSearch = () => {
            setActiveSection(previous => previous.deactivate());
            setExpanded(false);
        };
        searchStore.events.on('clearSearch', onClearSearch);
        return () => searchStore.events.off('clearSearch', onClearSearch);
    }, [searchStore, setActiveSection, setExpanded]);

    const [panelSize, setPanelSize] = React.useState<Size>({
        width: defaultWidth,
        height: defaultHeight,
    });
    const maxSize = React.useRef<Size>({
        width: Infinity,
        height: Infinity,
    });
    const resizePanel = (size: Size) => {
        const nextSize: Size = {
            width: Math.max(size.width, minWidth),
            height: Math.max(size.height, minHeight),
        };
        if (!(panelSize.width === nextSize.width && panelSize.height === nextSize.height)) {
            setPanelSize(nextSize);
        }
    };
    const updateMaxSize = (nextMaxSize: Size) => {
        maxSize.current = nextMaxSize;
        if (panelSize.width > nextMaxSize.width || panelSize.height > nextMaxSize.height) {
            setPanelSize({...panelSize});
        }
    };
    const effectiveSize: Size = {
        width: Math.min(panelSize.width, maxSize.current.width),
        height: Math.min(panelSize.height, maxSize.current.height),
    };

    const sectionsWithContext = React.useMemo(
        (): readonly SectionWithContext[] => sections.map((section): SectionWithContext => ({
            ...section,
            context: {
                searchStore,
                isSectionActive: section.key === activeSection?.key,
                setSectionActive: (active, searchExtra) => {
                    if (active) {
                        setActiveSection(previous => previous.activate(section.key, searchExtra));
                        setExpanded(true);
                        toggleInputRef.current?.focus();
                    } else {
                        setActiveSection(previous => previous.deactivate());
                        setExpanded(false);
                    }
                },
            }
        })),
        [sections, searchStore, activeSection, setActiveSection, setExpanded]
    );

    const {getCommandBus} = useWorkspace();

    const toggleInputRef = React.useRef<HTMLInputElement | null>(null);
    const onFocus = React.useCallback(({sectionKey}: UnifiedSearchCommands['focus']): void => {
        if (sectionKey) {
            if (sections.some(section => section.key === sectionKey)) {
                setActiveSection(previous => previous.activate(sectionKey));
            } else {
                console.warn(
                    `Unified search: cannot activate section that does not exists: ${sectionKey}`
                );
            }
        }
        toggleInputRef.current?.focus();
    }, [sections, setActiveSection]);

    const commands = getCommandBus(UnifiedSearchTopic);
    React.useEffect(() => {
        commands.on('focus', onFocus);
        return () => commands.off('focus', onFocus);
    }, [commands, onFocus]);

    useCanvasHotkey(hotkeyFocus, () => onFocus({}));

    const hasSearchQuery = (
        searchTerm.length > 0 ||
        activeSection.getSearchExtra() !== undefined
    );

    const onClickOutside = React.useCallback(() => {
        if (!hasSearchQuery) {
            setExpanded(false);
        }
    }, [hasSearchQuery, setExpanded]);

    return (
        <Dropdown className={CLASS_NAME}
            direction={direction}
            expanded={expanded}
            onClickOutside={onClickOutside}
            toggle={
                <SearchToggle inputRef={toggleInputRef}
                    searchTerm={searchTerm}
                    setSearchTerm={value => searchStore.change({value, action: 'input'})}
                    onSubmit={() => {/*...*/}}
                    onClear={
                        hasSearchQuery
                            ? () => searchStore.change({value: '', action: 'clear'})
                            : undefined
                    }
                    expanded={expanded}
                    setExpanded={nextExpanded => {
                        setActiveSection(previous => {
                            if (nextExpanded) {
                                if (previous.key === undefined) {
                                    const defaultSectionKey = findDefaultSectionKey();
                                    if (defaultSectionKey) {
                                        return previous.activate(defaultSectionKey);
                                    }
                                }
                            } else {
                                return previous.deactivate();
                            }
                            return previous;
                        });
                        setExpanded(nextExpanded);
                    }}
                    minWidth={minWidth}
                    panelSize={effectiveSize}
                />
            }>
            <SearchContent sections={sectionsWithContext}
                activeSectionKey={activeSection.key}
                onActivateSection={key => setActiveSection(previous => previous.switch(key))}
                dropUp={direction === 'up'}
                size={effectiveSize}
                minSize={{width: minWidth, height: minHeight}}
                offsetForMaxSize={{x: offsetWithMaxWidth, y: offsetWithMaxHeight}}
                onResize={resizePanel}
                setMaxSize={updateMaxSize}
            />
        </Dropdown>
    );
}

class ActiveSection {
    static readonly empty = new ActiveSection(undefined, undefined, {});

    private constructor(
        readonly key: string | undefined,
        readonly previousActive: string | undefined,
        readonly searchExtras: { readonly [sectionKey: string]: object | undefined }
    ) {}

    getSearchExtra(): object | undefined {
        const {key, searchExtras} = this;
        if ((
            key !== undefined &&
            Object.prototype.hasOwnProperty.call(searchExtras, key)
        )) {
            return searchExtras[key];
        }
        return undefined;
    }

    activate(key: string, extra?: object): ActiveSection {
        if (this.key === key && this.getSearchExtra() === extra) {
            return this;
        }
        const previousActive = this.key === key ? this.previousActive : this.key;
        const searchExtras = {...this.searchExtras, [key]: extra};
        return new ActiveSection(key, previousActive, searchExtras);
    }

    deactivate(): ActiveSection {
        return new ActiveSection(undefined, this.key, this.searchExtras);
    }

    switch(key: string): ActiveSection {
        if (this.key === key) {
            return this;
        }
        return new ActiveSection(key, this.key, this.searchExtras);
    }
}

function SearchToggle(props: {
    inputRef: React.RefObject<HTMLInputElement | null>,
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    onSubmit: () => void;
    onClear?: () => void;
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
    minWidth: number;
    panelSize?: Size;
}) {
    const {
        inputRef, searchTerm, setSearchTerm, onSubmit, onClear, expanded, setExpanded,
        minWidth, panelSize,
    } = props;
    const t = useTranslation();

    return (
        <div className={cx(`${CLASS_NAME}__toggle`)}
            style={{
                width: panelSize?.width,
            }}>
            <input
                ref={
                    /* For compatibility with React 19 typings */
                    inputRef as React.RefObject<HTMLInputElement>
                }
                type='text'
                className={`${CLASS_NAME}__search-input`}
                style={{minWidth}}
                placeholder={t.text('unified_search.input.placeholder')}
                name='reactodia-search'
                value={searchTerm}
                onClick={() => setExpanded(true)}
                onFocus={() => setExpanded(true)}
                onChange={e => setSearchTerm(e.currentTarget.value)}
                onKeyUp={e => {
                    switch (e.key) {
                        case 'Enter': {
                            if (searchTerm.length > 0) {
                                setExpanded(true);
                            }
                            onSubmit();
                            break;
                        }
                        case 'Escape': {
                            if (onClear) {
                                onClear();
                            } else {
                                setExpanded(false);
                            }
                            break;
                        }
                        default: {
                            setExpanded(true);
                            break;
                        }
                    }
                }}
            />
            {onClear ? (
                <button type='button'
                    title={t.text('unified_search.input_clear.title')}
                    className={`${CLASS_NAME}__clear-button`}
                    onClick={onClear}>
                </button>
            ) : expanded ? (
                <button type='button'
                    title={t.text('unified_search.input_collapse.title')}
                    className={`${CLASS_NAME}__collapse-button`}
                    onClick={() => setExpanded(false)}>
                </button>
            ) : (
                <div className={`${CLASS_NAME}__search-icon`}
                    onClick={() => inputRef.current?.focus()}
                />
            )}
        </div>
    );
}

function SearchContent(props: {
    sections: ReadonlyArray<SectionWithContext>;
    activeSectionKey: string | undefined;
    onActivateSection: (sectionKey: string) => void;
    dropUp: boolean;
    size: Size;
    minSize: Size;
    offsetForMaxSize: Vector;
    onResize: (size: Size) => void;
    setMaxSize: (size: Size) => void;
}) {
    const {
        sections, activeSectionKey, onActivateSection,
        dropUp, size, minSize, offsetForMaxSize, onResize, setMaxSize,
    } = props;

    const {canvas} = useCanvas();
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const panelBounds = React.useRef<{
        readonly bounds: Rect;
        readonly centeredByX: boolean;
        readonly centerOffsetX: number;
    }>(undefined);

    const updatePanelBounds = () => {
        if (!panelRef.current) {
            return;
        }
        const {x: rawX, y: rawY, width, height} = panelRef.current.getBoundingClientRect();
        const x = rawX + window.scrollX;
        const y = rawY + window.scrollY;
        const [alignmentX] = getParentDockAlignment(panelRef.current);
        const viewport = canvas.metrics.getViewportPageRect();
        panelBounds.current = {
            bounds: {x, y, width, height},
            // A workaround to have correct resizing behavior when the element
            // is aligned to the center without sacrificing performance to avoid
            // reading target bounds on every resize (causing a costly reflow)
            centeredByX: alignmentX === 'center',
            centerOffsetX: Math.max(
                x * 2 + width - viewport.x * 2 - viewport.width, 0
            ),
        };
    };

    useViewportResizeHandler(() => {
        if (!panelBounds.current) {
            updatePanelBounds();
        }

        if (panelBounds.current) {
            const {bounds, centeredByX, centerOffsetX} = panelBounds.current;
            const viewport = canvas.metrics.getViewportPageRect();
            const viewportRight = viewport.x + viewport.width;
            const viewportBottom = viewport.y + viewport.height;

            const proposedWidth = centeredByX
                ? viewport.width - centerOffsetX - offsetForMaxSize.x
                : viewportRight - bounds.x - offsetForMaxSize.x;

            const proposedHeight = dropUp
                ? bounds.y - viewport.y - offsetForMaxSize.y
                : viewportBottom - bounds.y - offsetForMaxSize.y;

            const maxWidth = Math.max(proposedWidth, minSize.width);
            const maxHeight = Math.max(proposedHeight, minSize.height);

            setMaxSize({width: maxWidth, height: maxHeight});
        }
    });
    
    const onDrag = (e: MouseEvent, dx: number, dy: number) => {
        if (panelBounds.current) {
            const {bounds, centeredByX} = panelBounds.current;
            onResize({
                width: bounds.width + dx * (centeredByX ? 2 : 1),
                height: bounds.height + (dropUp ? -dy : dy),
            });
        }
    };

    return (
        <div ref={panelRef}
            className={`${CLASS_NAME}__panel`}
            style={{
                width: size.width,
                height: size.height,
            }}>
            <div className={`${CLASS_NAME}__section-tabs`}>
                {sections.map(section => (
                    <button key={section.key}
                        className={cx(
                            `${CLASS_NAME}__section-tab`,
                            'reactodia-btn',
                            'reactodia-btn-default',
                            section.key === activeSectionKey ? 'active' : undefined,
                        )}
                        title={section.title}
                        onClick={() => onActivateSection(section.key)}>
                        {section.label}
                    </button>
                ))}
            </div>
            {sections.map(section => (
                <UnifiedSearchSectionContext.Provider key={section.key}
                    value={section.context}>
                    <div
                        className={cx(
                            `${CLASS_NAME}__section`,
                            section.context.isSectionActive
                                ? `${CLASS_NAME}__section--active` : undefined
                        )}>
                        {section.component}
                    </div>
                </UnifiedSearchSectionContext.Provider>
            ))}
            <DraggableHandle dock={dropUp ? 'n' : 's'}
                axis='y'
                onBeginDragHandle={updatePanelBounds}
                onDragHandle={onDrag}>
            </DraggableHandle>
            <DraggableHandle dock='e'
                axis='x'
                className={`${CLASS_NAME}__right-handle`}
                onBeginDragHandle={updatePanelBounds}
                onDragHandle={onDrag}>
            </DraggableHandle>
            <DraggableHandle dock={dropUp ? 'ne' : 'se'}
                axis='all'
                className={`${CLASS_NAME}__corner-handle`}
                onBeginDragHandle={updatePanelBounds}
                onDragHandle={onDrag}>
            </DraggableHandle>
        </div>
    );
}

function useViewportResizeHandler(handler: () => void) {
    const {canvas} = useCanvas();

    const sizeCheckerRef = React.useRef<() => void>(undefined);
    React.useLayoutEffect(() => {
        sizeCheckerRef.current = handler;
    });

    React.useLayoutEffect(() => {
        const debouncer = new Debouncer();
        const onViewportResize = () => {
            const sizeChecker = sizeCheckerRef.current;
            if (sizeChecker) {
                debouncer.call(sizeChecker);
            }
        };
        canvas.events.on('resize', onViewportResize);
        sizeCheckerRef.current?.();
        return () => {
            debouncer.dispose();
            canvas.events.off('resize', onViewportResize);
        };
    }, []);
}
